// src/pages/SellerIntelligence/IssueResolutionPanel.js
import { useState, useEffect, useContext, useRef, useCallback, memo } from 'react';
import styles from './IssueResolutionPanel.module.css';
import AuthContext from '../../../../context/AuthContext';
import useFixGeneration from '../hooks/useFixGeneration';
import useFixApplication from '../hooks/useFixApplication';

const MANUAL_FIX_CODES = new Set([
  '8541', '5995', '100898', '18940', '990003', '18027', '100581', '100232', '100019',
  '4005011', '4005013', '4005015', '4005006', '4005038', '4005012',
  '4005033', '4005007', '4005008', '4005035',
  '4005034', '4005002', '4005003', '4005001',
  '4005018', '4005019', '4000003', '4005039', '4005022', '4000000',
  '90188',
]);

const isManualFix = (code) => MANUAL_FIX_CODES.has(String(code));

// Claude sometimes returns suggested_fix as a list of steps instead of one string.
const parseNumberedSteps = (text) => {
  if (!text) return [];
  if (Array.isArray(text)) return text.map(String).filter(Boolean);
  text = String(text);
  const parts = text.split(/(?:^|\s)(?=\d+\.\s)/).map((s) => s.trim()).filter(Boolean);
  return parts.length <= 1 ? [text] : parts;
};

const getSeverityImpact = (severity) => {
  if (severity === 'FATAL') return 'This issue is preventing the listing from being visible to customers. Immediate action required.';
  if (severity === 'ERROR') return 'This issue is suppressing the listing from search results. Losing potential sales.';
  return 'This issue may affect listing visibility or search ranking if not addressed.';
};

const getSeverityUrgency = (severity) => {
  if (severity === 'FATAL') return 'This is a critical issue that may lead to listing removal if not fixed immediately.';
  if (severity === 'ERROR') return 'This issue may lead to account health warnings and possible listing suppression if not fixed promptly.';
  return 'Fixing this issue will improve listing quality and search ranking.';
};

// ── Skeleton / Spinner ────────────────────────────────────────────────────────

const SkeletonBlock = () => (
  <div className={styles.fixCard}>
    <div className={styles.fixCardHeader}>
      <div className={styles.skeletonLine} style={{ width: '120px', height: '22px' }} />
      <div className={styles.skeletonLine} style={{ width: '80%', height: '18px' }} />
    </div>
    <div className={styles.fixCardBody}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.skeletonLine} style={{ width: '160px', height: '16px' }} />
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.skeletonLine} style={{ width: '100%', height: '14px' }} />
          <div className={styles.skeletonLine} style={{ width: '90%', height: '14px' }} />
          <div className={styles.skeletonLine} style={{ width: '70%', height: '14px' }} />
        </div>
      </div>
    </div>
  </div>
);

const Spinner = () => (
  <svg className={styles.spinner} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" strokeLinecap="round" />
  </svg>
);

// ── FixSection — isolated per-issue fix UI, memoized to prevent re-renders ────

const FixSection = memo(({ issueCode, state = { status: 'idle' }, issue, onGenerate, onApply, onCancel, onEdit }) => {
  if (state.status === 'success') {
    return (
      <div className={styles.fixActionRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
        <div className={styles.fixSuccessBadge}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <circle cx="7" cy="7" r="7" fill="#22c55e" />
            <path d="M4 7l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Applied to Amazon — changes may take a few minutes to reflect.
        </div>
        {state.submissionId && (
          <span className={styles.fixManualNote}>Submission ID: {state.submissionId}</span>
        )}
      </div>
    );
  }

  if (state.status === 'generating' || state.status === 'applying') {
    return (
      <div className={styles.fixActionRow}>
        <button type="button" className={styles.fixAiBtn} disabled>
          <Spinner />
          {state.status === 'generating' ? 'Generating fix…' : 'Applying fix…'}
        </button>
      </div>
    );
  }

  if (state.status === 'manual') {
    return (
      <div className={styles.fixActionRow}>
        <div className={styles.fixManualNote}>{state.manualMsg}</div>
        <a href={state.manualUrl} target="_blank" rel="noopener noreferrer" className={styles.fixSellerCentralBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M6 2H2a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V8M9 1h4m0 0v4m0-4L6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Fix in Seller Central
        </a>
      </div>
    );
  }

  if (state.status === 'preview') {
    return (
      <div className={styles.fixPreviewBlock}>
        <div className={styles.fixPreviewLabel}>
          Attribute: <span className={styles.fixPreviewAttr}>{state.attribute}</span>
        </div>
        {state.currentValue && (
          <div className={styles.fixPreviewCurrent}>
            <div className={styles.fixPreviewSectionLabel}>Current value</div>
            <div className={styles.fixPreviewCurrentText}>{state.currentValue}</div>
          </div>
        )}
        {state.reason && (
          <div className={styles.fixPreviewCurrent}>
            <div className={styles.fixPreviewSectionLabel}>Why this fix</div>
            <div className={styles.fixPreviewCurrentText}>{state.reason}</div>
          </div>
        )}
        <div className={styles.fixPreviewProposed}>
          <div className={styles.fixPreviewSectionLabel}>AI-proposed fix (editable)</div>
          <textarea
            className={styles.fixPreviewTextarea}
            value={state.editableValue}
            onChange={(e) => onEdit(issueCode, e.target.value)}
            rows={state.editableValue && state.editableValue.length > 200 ? 6 : 3}
          />
        </div>
        {state.amazonErrors?.length > 0 && (
          <div className={styles.amazonErrorBlock}>
            <div className={styles.amazonErrorTitle}>Amazon rejected the update — edit the value above and retry:</div>
            {state.amazonErrors.map((ae, i) => (
              <div key={i} className={styles.amazonErrorItem}>
                {ae.code && <span className={styles.amazonErrorCode}>{ae.code}</span>}
                <span className={styles.amazonErrorMsg}>{ae.message || ae.attributeNames?.join(', ')}</span>
              </div>
            ))}
          </div>
        )}
        {state.errorMsg && <div className={styles.fixErrorMsg}>{state.errorMsg}</div>}
        <div className={styles.fixPreviewActions}>
          <button type="button" className={styles.fixAiBtn} onClick={() => onApply(issueCode, issue)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7l4 4 6-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {state.amazonErrors ? 'Retry Fix' : 'Apply Fix'}
          </button>
          <button type="button" className={styles.fixCancelBtn} onClick={() => onCancel(issueCode)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // idle or error
  const manual = isManualFix(issue.code) || !(issue.attributes?.length > 0);
  if (manual) {
    return (
      <div className={styles.fixActionRow}>
        <a href="https://sellercentral.amazon.in/inventory" target="_blank" rel="noopener noreferrer" className={styles.fixSellerCentralBtn}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M6 2H2a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1V8M9 1h4m0 0v4m0-4L6 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Fix in Seller Central
        </a>
      </div>
    );
  }

  return (
    <div className={styles.fixActionRow}>
      {state.status === 'error' && <span className={styles.fixErrorMsg}>{state.errorMsg}</span>}
      <button type="button" className={styles.fixAiBtn} onClick={() => onGenerate(issueCode, issue)}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M8.399 6.651c-2.713-.618-3.118-1.023-3.737-3.737A.174.174 0 004.5 2.77a.174.174 0 00-.163.144c-.62 2.714-1.024 3.118-3.737 3.737a.174.174 0 000 .347c2.713.619 3.117 1.023 3.737 3.737a.174.174 0 00.163.143.174.174 0 00.162-.143c.62-2.714 1.024-3.118 3.737-3.737a.174.174 0 000-.347z" fill="currentColor"/>
          <path d="M11.766 2.278c-1.45-.332-1.648-.53-1.98-1.98a.12.12 0 00-.237 0c-.332 1.45-.53 1.648-1.98 1.98a.12.12 0 000 .237c1.45.332 1.648.53 1.98 1.98a.12.12 0 00.237 0c.332-1.45.53-1.648 1.98-1.98a.12.12 0 000-.237z" fill="currentColor"/>
        </svg>
        {state.status === 'error' ? 'Retry Fix' : 'Fix with AI'}
      </button>
    </div>
  );
});

// ── IssueCard — single collapsible issue row ──────────────────────────────────

const IssueCard = memo(({ issue, isExpanded, aiAnalysis, fixInfo, fixState, onToggle, onGenerate, onApply, onCancel, onEdit }) => {
  const impactContent      = aiAnalysis?.impact_assessment ?? null;
  const explanationContent = aiAnalysis?.error_explanation ?? null;
  const suggestedFixContent = aiAnalysis?.suggested_fix ?? null;

  return (
    <div className={styles.fixCard}>
      <div
        className={`${styles.fixCardHeader} ${isExpanded ? styles.fixCardHeaderOpen : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className={`${styles.fixCardTitle} ${isExpanded ? styles.fixCardTitleOpen : ''}`}>
          {issue.message || `Issue #${issue.code}`}
        </div>
        <svg
          className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
          width="15" height="9" viewBox="0 0 15 9" fill="none" aria-hidden
        >
          <path d="M1.7625 0L7.5 5.56275L13.2375 0L15 1.71255L7.5 9L0 1.71255L1.7625 0Z" fill="#737373"/>
        </svg>
      </div>

      {isExpanded && (
        <div className={styles.fixCardBody}>
          {explanationContent && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect width="16" height="16" rx="8" fill="#B1D7FF"/>
                  <path d="M8 4c.414 0 .75.336.75.75v3.5a.75.75 0 01-1.5 0v-3.5C7.25 4.336 7.586 4 8 4zm0 6.5a.75.75 0 110 1.5.75.75 0 010-1.5z" fill="white"/>
                </svg>
                <span className={styles.sectionLabel}>ERROR EXPLANATION</span>
              </div>
              <div className={styles.sectionContent}>
                <p className={styles.sectionParagraph}>{explanationContent}</p>
              </div>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect width="16" height="16" rx="8" fill="#F4B6B2"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M7.24 4H8.76c.298 0 .526.255.493.55L8.788 8.668a.29.29 0 01-.292.24H7.504a.29.29 0 01-.289-.27L6.748 4.552A.487.487 0 017.239 4zM8 10.035c.543 0 .982.44.982.982a.982.982 0 01-1.965 0c0-.542.44-.982.982-.982z" fill="white"/>
              </svg>
              <span className={styles.sectionLabel}>IMPACT ASSESSMENT</span>
            </div>
            <div className={styles.sectionContent}>
              {impactContent ? (
                <p className={styles.sectionParagraph}>{impactContent}</p>
              ) : (
                <>
                  <p className={styles.sectionParagraph}>{getSeverityImpact(issue.severity)}</p>
                  <p className={styles.sectionParagraph}>{getSeverityUrgency(issue.severity)}</p>
                </>
              )}
              {!impactContent && issue.attributes?.length > 0 && (
                <p className={styles.sectionParagraph}>Affected attributes: {issue.attributes.join(', ')}</p>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M0.972 4.551H2.229c.535 0 .972.437.972.972v9.289a.972.972 0 01-.972.972H.972A.972.972 0 010 14.812V5.523c0-.535.437-.972.972-.972zM16 9.268c0-.671-.599-1.221-1.33-1.221.683 0 1.33-.506 1.33-1.221 0-.671-.599-1.221-1.33-1.221H10.07c.338-1.584.838-2.709.13-4.281C9.825.489 9.181-.192 8.3.049c-.778.212-.643 2.489-1.302 3.897-.251.537-1.302 2.081-1.992 2.084l-.99.004v8.505h.484c.826 0 1.344.82 2.18.834h7.118c.683 0 1.33-.507 1.33-1.221 0-.672-.599-1.222-1.33-1.222h1.27c.683 0 1.33-.507 1.33-1.221 0-.672-.599-1.222-1.33-1.222C15.401 10.49 16 9.983 16 9.268z" fill="#B1D7FF"/>
              </svg>
              <span className={styles.sectionLabel}>SUGGESTED FIX</span>
            </div>
            <div className={styles.sectionContent}>
              {suggestedFixContent ? (
                <ol className={styles.stepsList}>
                  {parseNumberedSteps(suggestedFixContent).map((step, sIdx) => (
                    <li key={sIdx} className={styles.stepItem}>{step.replace(/^\d+\.\s*/, '')}</li>
                  ))}
                </ol>
              ) : fixInfo?.steps ? (
                fixInfo.steps.map((step, sIdx) => (
                  <p key={sIdx} className={styles.sectionParagraph}>{step}</p>
                ))
              ) : (
                <p className={styles.sectionParagraph}>Review and update the affected attributes to resolve this issue.</p>
              )}
              {!suggestedFixContent && fixInfo?.estimated_time && (
                <p className={styles.sectionParagraph}>Estimated time: {fixInfo.estimated_time}</p>
              )}
            </div>
          </div>

          <FixSection
            issueCode={issue.code}
            state={fixState}
            issue={issue}
            onGenerate={onGenerate}
            onApply={onApply}
            onCancel={onCancel}
            onEdit={onEdit}
          />
        </div>
      )}
    </div>
  );
});

// ── Main panel ────────────────────────────────────────────────────────────────

const IssueResolutionPanel = ({ isOpen, onClose, row, analysisData, analysisLoading }) => {
  const authCtx = useContext(AuthContext);

  const productName = row?.productName ?? 'Unknown Product';
  const skuId       = row?.skuId ?? '-';
  const asin        = row?.asin ?? '-';
  const productType = row?.productType ?? '';

  const { generate } = useFixGeneration(authCtx.token, { skuId, asin, productName, productType });
  const { apply }    = useFixApplication(authCtx.token, { skuId, asin, productName, productType });

  // fixState keyed by issue.code (stable) instead of array index
  const [fixState, setFixState] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const [showCloseBtn, setShowCloseBtn] = useState(true);
  const contentRef = useRef(null);

  useEffect(() => {
    setFixState({});
    setExpandedCards({ [row?.issues?.[0]?.code]: true });
    setShowCloseBtn(true);
  }, [row]);

  useEffect(() => {
    if (!isOpen) return;
    const el = contentRef.current;
    if (!el) return;
    const handleScroll = () => setShowCloseBtn(el.scrollTop < el.clientHeight * 0.02);
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const patchIssueState = useCallback((code, patch) =>
    setFixState((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || { status: 'idle' }), ...patch },
    })), []);

  const handleToggleCard = useCallback((code) =>
    setExpandedCards((prev) => ({ ...prev, [code]: !prev[code] })), []);

  const handleGenerateFix = useCallback(async (code, issue) => {
    patchIssueState(code, { status: 'generating', errorMsg: null });
    try {
      const d = await generate(issue);
      if (d.fix_type === 'manual') {
        patchIssueState(code, { status: 'manual', manualMsg: d.message, manualUrl: d.seller_central_url });
      } else {
        patchIssueState(code, {
          status: 'preview',
          attribute: d.attribute,
          currentValue: d.current_value,
          proposedValue: d.proposed_value,
          editableValue: d.proposed_value,
          reason: d.reason || '',
        });
      }
    } catch (err) {
      patchIssueState(code, { status: 'error', errorMsg: err.message || 'Failed to generate fix' });
    }
  }, [generate, patchIssueState]);

  const handleApplyFix = useCallback(async (code, issue) => {
    const state = fixState[code];
    if (!state) return;
    patchIssueState(code, { status: 'applying', errorMsg: null, amazonErrors: null });
    try {
      const result = await apply(state, issue);
      if (result.amazonIssues) {
        patchIssueState(code, { status: 'preview', amazonErrors: result.amazonIssues });
      } else {
        patchIssueState(code, { status: 'success', submissionId: result.submissionId });
      }
    } catch (err) {
      patchIssueState(code, { status: 'error', errorMsg: err.message || 'Failed to apply fix' });
    }
  }, [fixState, apply, patchIssueState]);

  const handleCancelPreview = useCallback((code) =>
    patchIssueState(code, { status: 'idle' }), [patchIssueState]);

  const handleEditValue = useCallback((code, value) =>
    patchIssueState(code, { editableValue: value }), [patchIssueState]);

  if (!isOpen) return null;

  const thumb         = row?.thumb ?? 'https://placehold.co/72x72';
  const status        = row?.status ?? 'Suppressed';
  const priority      = row?.priority ?? 'high';
  const fixesCount    = row?.fixesCount ?? 0;
  const issues        = row?.issues ?? [];
  const actionableFixes = row?.actionableFixes ?? {};

  const analysisMap = {};
  if (Array.isArray(analysisData)) {
    analysisData.forEach((a) => { if (a.code) analysisMap[a.code] = a; });
  }

  const priorityLabel = priority === 'high' ? 'High Priority' : priority === 'medium' ? 'Medium Priority' : 'Low Priority';

  return (
    <>
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <div className={styles.panel} role="dialog" aria-labelledby="issue-resolution-title" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.closeBtn + (!showCloseBtn ? ' ' + styles.closeBtnHidden : '')}
          onClick={onClose}
          aria-label="Close panel"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050"/>
          </svg>
        </button>

        <div className={styles.content} ref={contentRef}>
          <div className={styles.header}>
            <h2 id="issue-resolution-title" className={styles.title}>Issue Resolution</h2>
            <div className={styles.subtitleRow}>
              <p className={styles.subtitle}>Suggested fixes for the listing, review and apply them.</p>
              <div className={styles.priorityBadge}>
                <span className={`${styles.priorityDot} ${
                  priority === 'high' ? styles.priorityHigh :
                  priority === 'medium' ? styles.priorityMedium : styles.priorityLow
                }`} />
                <span className={styles.priorityText}>{priorityLabel}</span>
              </div>
            </div>
          </div>

          <div className={styles.productCard}>
            <div className={styles.productCardInner}>
              <div className={styles.productThumb}><img src={thumb} alt="" /></div>
              <div className={styles.productInfo}>
                <div className={styles.productName}>{productName}</div>
                <div className={styles.productBadges}>
                  <span className={styles.skuBadge}>SKU ID : {skuId}</span>
                  <span className={styles.statusBadge}>{status}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.fixesHeader}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <g opacity="0.7">
                <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
                <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
              </g>
            </svg>
            <span className={styles.fixesHeaderText}>{fixesCount} fixes required</span>
          </div>

          <div className={styles.fixList}>
            {analysisLoading ? (
              <><SkeletonBlock /><SkeletonBlock /></>
            ) : (
              issues.map((issue) => (
                <IssueCard
                  key={issue.code}
                  issue={issue}
                  isExpanded={!!expandedCards[issue.code]}
                  aiAnalysis={analysisMap[issue.code]}
                  fixInfo={actionableFixes[issue.code]}
                  fixState={fixState[issue.code]}
                  onToggle={() => handleToggleCard(issue.code)}
                  onGenerate={handleGenerateFix}
                  onApply={handleApplyFix}
                  onCancel={handleCancelPreview}
                  onEdit={handleEditValue}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default IssueResolutionPanel;
